import './accordion.scss';

import { useCallback, useEffect, useState } from 'react';

import { makeKeyDownHandler } from '../../data/utils/make-key-down-handler';

type AccordionItem = {
  key: string;
  title: JSX.Element | string;
  count: number;
  content: JSX.Element | string;
};

type AccordionProps = {
  items: AccordionItem[];
  onChange?: (value: string[]) => void;
};

export function Accordion({ items, onChange }: AccordionProps) {
  const [selected, setSelected] = useState<string[] | null>();

  useEffect(() => {
    if (!selected) {
      const keys = items.map(x => x.key);
      setSelected(keys);
    }
  }, [selected, items]);

  const onHandleToggle = useCallback(
    (value: string) => {
      setSelected(prevState => {
        const newState = [...(prevState ?? [])];

        const index = newState.findIndex(option => option === value);

        if (index === -1) {
          newState.push(value);
        } else {
          newState.splice(index, 1);
        }

        onChange?.(newState);

        return newState;
      });
    },
    [onChange]
  );

  if (items.length === 0) return null;

  return (
    <>
      {items.map(option => (
        <div
          key={option.key}
          className={`accordion${
            selected?.includes(option.key) ? ` active` : ``
          }`}
        >
          <div
            className="accordion__header"
            role="button"
            tabIndex={0}
            onClick={() => onHandleToggle(option.key)}
            onKeyDown={makeKeyDownHandler(() => onHandleToggle(option.key))}
          >
            <h2>
              {option.title} ({option.count})
            </h2>
          </div>
          <div className="accordion__content">{option.content}</div>
        </div>
      ))}
    </>
  );
}
